// Les sujets des articles, leurs sous-thèmes et leurs adresses.
//
// Un sujet dit de quoi parle l'article, la catégorie dit à qui il s'adresse.
// La page /articles filtrait par catégorie : un article d'actualité sur l'IA
// s'adresse à tout le monde, donc « Développeurs », « Entrepreneurs » et
// « No-code » rendaient la même liste.
//
// Chaque sujet a sa page (/articles/entreprendre), chaque sous-thème aussi
// (/articles/intelligence-artificielle/claude), et la suite d'une liste vit
// sous /page/2. Ce sont de vraies pages, rendues par le serveur : un filtre en
// paramètre d'adresse n'était lu ni par Google ni par les robots d'IA.
//
// La liste des identifiants vit aussi côté back, dans
// src/Service/ArticleTopics.php, qui refuse tout ce qui n'y figure pas. Ajouter
// un sujet se fait aux deux endroits, avec ses libellés articles_topic_* et
// articles_subtopic_* dans messages/.
//
// Un sous-thème peut aussi naître sans déploiement : quand trois articles d'un
// sujet parlent du même outil, l'administration propose de lui donner sa page
// (components/admin/ArticleTopics/SubtopicProposals.jsx). Ceux-là vivent en
// base, avec leur libellé, et mergeArticleTopics() les ajoute à la liste
// ci-dessous. Toutes les fonctions prennent donc la liste en dernier argument ;
// sans lui, elles ne connaissent que les sous-thèmes d'origine.

// L'ordre est celui de la page : ce qui concerne l'activité d'abord, parce que
// le lecteur visé travaille à son compte.
export const ARTICLE_TOPICS = [
    {
        id: "business",
        icon: "case-minimalistic-linear",
        slug: { fr: "entreprendre", en: "business" },
        subtopics: [],
    },
    {
        id: "ai",
        icon: "magic-stick-3-linear",
        slug: { fr: "intelligence-artificielle", en: "artificial-intelligence" },
        subtopics: [
            { id: "claude", slug: { fr: "claude", en: "claude" } },
            { id: "openai", slug: { fr: "chatgpt-openai", en: "chatgpt-openai" } },
            { id: "hermes", slug: { fr: "hermes-agent", en: "hermes-agent" } },
            { id: "other", slug: { fr: "autres-outils", en: "other-tools" } },
        ],
    },
    {
        id: "web",
        icon: "programming-linear",
        slug: { fr: "developpement-web", en: "web-development" },
        subtopics: [
            { id: "react", slug: { fr: "react-nextjs", en: "react-nextjs" } },
            { id: "css", slug: { fr: "css", en: "css" } },
            { id: "javascript", slug: { fr: "javascript", en: "javascript" } },
            { id: "git", slug: { fr: "git", en: "git" } },
            { id: "effects", slug: { fr: "effets", en: "effects" } },
        ],
    },
    {
        id: "learning",
        icon: "notebook-linear",
        slug: { fr: "apprendre", en: "learning" },
        subtopics: [],
    },
];

const BY_ID = new Map(ARTICLE_TOPICS.map((topic) => [topic.id, topic]));

const lang = (locale) => (locale === "en" ? "en" : "fr");

// Les sujets où l'administration peut créer un sous-thème, et le fourre-tout
// d'où sortent ses articles. Même liste que ArticleTopics::PROPOSABLE au back.
export const PROPOSABLE_TOPICS = ["ai", "web"];
export const CATCH_ALL_SUBTOPIC = "other";

// Les mots qu'une adresse de sujet réserve déjà.
const RESERVED_SLUGS = ["all", "page"];

/** Un slug de sous-thème : minuscules, chiffres et tirets, comme au back. */
export const isSubtopicSlug = (value, max = 64) =>
    typeof value === "string" &&
    value.length >= 2 &&
    value.length <= max &&
    /^[a-z0-9]+(-[a-z0-9]+)*$/.test(value) &&
    !RESERVED_SLUGS.includes(value);

/** « Tailwind CSS » donne « tailwind-css ». Rend "" quand il ne reste rien. */
export const slugifySubtopic = (name, max = 64) =>
    String(name || "")
        .normalize("NFD")
        .replace(/[\u0300-\u036f]/g, "")
        .toLowerCase()
        .replace(/[^a-z0-9]+/g, "-")
        .replace(/^-+|-+$/g, "")
        .slice(0, max)
        .replace(/-+$/, "");

/**
 * La liste des sujets avec, en plus, les sous-thèmes créés depuis
 * l'administration (lib/articles/topicData.js les lit au back).
 *
 * Un sous-thème créé se range avant le fourre-tout, qui reste en dernier, et
 * porte son libellé (`label`) : il n'a pas de clé dans messages/.
 *
 * @param {Array<{id: string, topic: string, slug: {fr: string, en: string}, label: {fr: string, en: string}}>} created
 */
export function mergeArticleTopics(created) {
    if (!Array.isArray(created) || created.length === 0) return ARTICLE_TOPICS;

    return ARTICLE_TOPICS.map((topic) => {
        const extras = created
            .filter(
                (item) =>
                    item?.topic === topic.id &&
                    item.id &&
                    !topic.subtopics.some((own) => own.id === item.id),
            )
            .map(({ id, slug, label }) => ({ id, slug, label }));
        if (extras.length === 0) return topic;

        const subtopics = [...topic.subtopics];
        const end = subtopics.findIndex((item) => item.id === CATCH_ALL_SUBTOPIC);
        subtopics.splice(end === -1 ? subtopics.length : end, 0, ...extras);

        return { ...topic, subtopics };
    });
}

const find = (topics, id) =>
    topics === ARTICLE_TOPICS
        ? BY_ID.get(id) || null
        : topics.find((topic) => topic.id === id) || null;

export const getArticleTopic = (id, topics = ARTICLE_TOPICS) => find(topics, id);

export const isArticleTopic = (id) => BY_ID.has(id);

export const isArticleSubtopic = (topic, subtopic, topics = ARTICLE_TOPICS) =>
    Boolean(find(topics, topic)?.subtopics.some((item) => item.id === subtopic));

/** Tous les identifiants de sous-thèmes, pour le schéma de l'outil de classement. */
export const articleSubtopicIds = (topics = ARTICLE_TOPICS) =>
    topics.flatMap((topic) => topic.subtopics.map((item) => item.id));

export const ARTICLE_SUBTOPIC_IDS = articleSubtopicIds();

/**
 * Le nom d'un sous-thème : son libellé quand il a été créé depuis
 * l'administration, sa clé articles_subtopic_* sinon.
 *
 * @param {Function} t La traduction à la racine des messages.
 */
export function articleSubtopicLabel(t, topicId, subtopicId, locale, topics = ARTICLE_TOPICS) {
    const subtopic = find(topics, topicId)?.subtopics.find((item) => item.id === subtopicId);

    return subtopic?.label?.[lang(locale)] || t(`articles_subtopic_${subtopicId}`);
}

/**
 * L'adresse d'un sujet, d'un sous-thème ou d'une de leurs pages, sans préfixe
 * de langue (le composant Link et getPathname l'ajoutent).
 *
 * @returns {string} "/articles" quand le sujet est inconnu ou absent.
 */
export function articleTopicPath(
    locale,
    topicId,
    subtopicId = null,
    page = 1,
    topics = ARTICLE_TOPICS,
) {
    const topic = find(topics, topicId);
    if (!topic) return "/articles";

    const parts = ["/articles", topic.slug[lang(locale)]];

    const subtopic = topic.subtopics.find((item) => item.id === subtopicId);
    if (subtopic) parts.push(subtopic.slug[lang(locale)]);

    if (page > 1) parts.push("page", String(page));

    return parts.join("/");
}

/**
 * Lit les segments d'une adresse de sujet.
 *
 * Les segments sont reconnus dans les deux langues : le sélecteur de langue
 * garde les segments de la page d'où l'on vient, et un lien français collé
 * dans une page anglaise doit aboutir. `canonical` dit alors où rediriger.
 *
 * @param {string} locale
 * @param {string[]} segments Ce qui suit /articles/ dans l'adresse.
 * @param {Array} [topics] La liste fusionnée, pour reconnaître aussi un
 *        sous-thème créé depuis l'administration.
 * @returns {{topic: string, subtopic: string|null, page: number, canonical: boolean}|null}
 *          null quand l'adresse ne désigne aucun sujet : la page répond 404.
 */
export function parseArticleTopicSegments(locale, segments, topics = ARTICLE_TOPICS) {
    const rest = [...(segments || [])];
    const matches = (slug, value) => slug.fr === value || slug.en === value;

    const topic = topics.find((item) => matches(item.slug, rest[0]));
    if (!topic) return null;
    rest.shift();

    let subtopic = null;
    if (rest.length && rest[0] !== "page") {
        subtopic = topic.subtopics.find((item) => matches(item.slug, rest[0])) || null;
        if (!subtopic) return null;
        rest.shift();
    }

    let page = 1;
    if (rest.length) {
        // « /page/1 » et « /page/02 » ne sont pas des adresses : la première
        // page n'a pas de suffixe, et un numéro s'écrit d'une seule façon.
        if (rest.length !== 2 || rest[0] !== "page" || !/^[1-9]\d*$/.test(rest[1])) return null;
        page = parseInt(rest[1], 10);
        if (page < 2) return null;
    }

    const expected = articleTopicPath(locale, topic.id, subtopic?.id, page, topics);

    return {
        topic: topic.id,
        subtopic: subtopic?.id || null,
        page,
        canonical: expected === `/articles/${(segments || []).join("/")}`,
    };
}

// Les anciens liens filtraient par catégorie (`/articles?category=developers`).
// Ils ouvrent le sujet le plus proche ; « nocode » n'a pas d'équivalent et
// reste sur la vue d'ensemble.
const LEGACY_CATEGORY_TOPIC = { developers: "web", entrepreneurs: "business" };

export const topicFromLegacyCategory = (category) => LEGACY_CATEGORY_TOPIC[category] || null;
